import { useAuth } from "@/auth/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { useMutateData } from "@/hooks/useMutation";
import { useQueryClient } from "@tanstack/react-query";
import { FolderPlus } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

const PROJECTS_ROOT_DEFAULT_KEY = "projects-root";
const INTERVENANTS_ROOT_DEFAULT_KEY = "intervenants-root";
const PROGRAM_ROOT_DEFAULT_KEY_PREFIX = "program-";

type FolderTarget = {
  kind: "porteur" | "project" | "intervenant";
  id: number;
  parentFolderId?: number | null;
  defaultKey?: string;
  reuseByDefaultKey?: boolean;
  forcedDocumentTypes?: number[];
  forceTypesFromCurrentFolder?: boolean;
  label: string;
  helper: string;
  disabled?: boolean;
  disabledReason?: string;
};

type AddDocumentFolderButtonProps = {
  requireChildFolder?: boolean;
  ownerId?: number | null;
};

type DocumentsCacheSnapshot = Array<{
  queryKey: readonly unknown[];
  data: unknown;
}>;

const addFolderToCollection = (
  collection: unknown,
  folder: any,
  target: FolderTarget,
): unknown => {
  if (Array.isArray(collection)) {
    if (target.parentFolderId) {
      return collection.map((currentFolder: any) => ({
        ...currentFolder,
        children:
          Number(currentFolder?.id) === Number(target.parentFolderId)
            ? [folder, ...(Array.isArray(currentFolder?.children) ? currentFolder.children : [])]
            : addFolderToCollection(currentFolder?.children ?? [], folder, target),
      }));
    }

    return [folder, ...collection];
  }

  if (collection && typeof collection === "object") {
    return Object.fromEntries(
      Object.entries(collection).map(([key, value]) => {
        if (key === "hiddenDefaults") {
          return [key, value];
        }

        if (key === target.kind) {
          const scopeFolders =
            value && typeof value === "object" && !Array.isArray(value)
              ? value as Record<string, unknown>
              : {};
          const scopeKey = String(target.id);
          return [
            key,
            {
              ...scopeFolders,
              [scopeKey]: addFolderToCollection(
                Array.isArray(scopeFolders[scopeKey]) ? scopeFolders[scopeKey] : [],
                folder,
                target,
              ),
            },
          ];
        }

        return [key, value];
      }),
    );
  }

  return collection;
};

const replaceFolderInCollection = (
  collection: unknown,
  optimisticId: number,
  nextFolder: any,
): unknown => {
  if (Array.isArray(collection)) {
    return collection.map((folder: any) => ({
      ...(Number(folder?.id) === Number(optimisticId) ? { ...folder, ...nextFolder } : folder),
      children: replaceFolderInCollection(folder?.children ?? [], optimisticId, nextFolder),
    }));
  }

  if (collection && typeof collection === "object") {
    return Object.fromEntries(
      Object.entries(collection).map(([key, value]) => [
        key,
        key === "hiddenDefaults"
          ? value
          : replaceFolderInCollection(value, optimisticId, nextFolder),
      ]),
    );
  }

  return collection;
};

const AddDocumentFolderButton = ({
  requireChildFolder = false,
  ownerId = null,
}: AddDocumentFolderButtonProps) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<number[]>([]);
  const queryClient = useQueryClient();

  const folderTarget = useMemo<FolderTarget | null>(() => {
    const pathname = window.location.pathname;
    const projectIds = (searchParams.get("projects") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const programIds = (searchParams.get("programs") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const intervenantIds = (searchParams.get("intervenants") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const folderIds = (searchParams.get("folderIds") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const scope = searchParams.get("scope");
    const categoryIds = (searchParams.get("categories") ?? "")
      .split(",")
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);
    const currentTypes = (searchParams.get("types") ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    const pathParts = pathname.split("/");
    const projectPathId =
      pathParts[1] === "dashboard" && pathParts[2] === "project"
        ? Number(pathParts[3])
        : null;

    const parentFolderId = folderIds.length === 1 ? folderIds[0] : null;
    const forcedDocumentTypes = parentFolderId ? [] : categoryIds;
    const isPhotosFolder = !parentFolderId && currentTypes.includes("image");

    if (projectIds.length > 1 || intervenantIds.length > 1 || folderIds.length > 1) {
      return {
        kind: "porteur",
        id: Number(user?.id ?? 0),
        label: "contexte courant",
        helper: "Sélection unique requise",
        disabled: true,
        disabledReason:
          "Affinez d’abord la sélection à un seul projet, intervenant ou dossier pour créer un nouveau dossier au bon endroit.",
      };
    }

    if (scope === "projects" && projectIds.length === 0 && !parentFolderId) {
      return {
        kind: "porteur",
        id: Number(ownerId ?? user?.id ?? 0),
        defaultKey: PROJECTS_ROOT_DEFAULT_KEY,
        label: "Mes Programmes",
        helper: "Le dossier sera créé dans Mes Programmes.",
      };
    }

    if (scope === "intervenants" && intervenantIds.length === 0 && !parentFolderId) {
      return {
        kind: "porteur",
        id: Number(ownerId ?? user?.id ?? 0),
        defaultKey: INTERVENANTS_ROOT_DEFAULT_KEY,
        label: "Dossier intervenant",
        helper: "Le dossier sera créé dans Dossier intervenant.",
      };
    }

    if (programIds.length === 1 && projectIds.length === 0) {
      return {
        kind: "porteur",
        id: Number(ownerId ?? user?.id ?? 0),
        defaultKey: `${PROGRAM_ROOT_DEFAULT_KEY_PREFIX}${programIds[0]}`,
        label: "ce programme",
        helper: "Le dossier sera créé dans le programme actuellement ouvert.",
      };
    }

    if (projectIds.length === 1) {
      if (isPhotosFolder) {
        return {
          kind: "project",
          id: projectIds[0],
          parentFolderId,
          label: "ce dossier projet",
          helper: "La création de sous-dossiers dans Photos n’est pas encore prise en charge.",
          disabled: true,
          disabledReason: "Impossible de créer un dossier directement dans Photos pour le moment.",
        };
      }

      return {
        kind: "project",
        id: projectIds[0],
        parentFolderId,
        forcedDocumentTypes,
        forceTypesFromCurrentFolder: forcedDocumentTypes.length > 0,
        label: parentFolderId ? "ce dossier projet" : "ce projet",
        helper: parentFolderId
          ? "Le dossier sera créé dans le dossier actuellement ouvert."
          : forcedDocumentTypes.length > 0
            ? "Le dossier sera créé dans le dossier actuellement ouvert et héritera de ses types de document."
            : "Le dossier sera créé à la racine du projet actuellement affiché.",
      };
    }

    if (projectPathId && Number.isFinite(projectPathId) && projectPathId > 0) {
      return {
        kind: "project",
        id: projectPathId,
        parentFolderId,
        forcedDocumentTypes,
        forceTypesFromCurrentFolder: forcedDocumentTypes.length > 0,
        label: parentFolderId ? "ce dossier projet" : "ce projet",
        helper: parentFolderId
          ? "Le dossier sera créé dans le dossier actuellement ouvert."
          : forcedDocumentTypes.length > 0
            ? "Le dossier sera créé dans le dossier actuellement ouvert et héritera de ses types de document."
            : "Le dossier sera créé à la racine du projet en cours.",
      };
    }

    if (intervenantIds.length === 1) {
      if (isPhotosFolder) {
        return {
          kind: "intervenant",
          id: intervenantIds[0],
          parentFolderId,
          label: "ce dossier intervenant",
          helper: "La création de sous-dossiers dans Photos n’est pas encore prise en charge.",
          disabled: true,
          disabledReason: "Impossible de créer un dossier directement dans Photos pour le moment.",
        };
      }

      return {
        kind: "intervenant",
        id: intervenantIds[0],
        parentFolderId,
        forcedDocumentTypes,
        forceTypesFromCurrentFolder: forcedDocumentTypes.length > 0,
        label: parentFolderId ? "ce dossier intervenant" : "cet intervenant",
        helper: parentFolderId
          ? "Le dossier sera créé dans le dossier actuellement ouvert."
          : forcedDocumentTypes.length > 0
            ? "Le dossier sera créé dans le dossier actuellement ouvert et héritera de ses types de document."
            : "Le dossier sera créé à la racine de l’intervenant actuellement affiché.",
      };
    }

    const currentUserId = Number(ownerId ?? user?.id ?? 0);
    if (!currentUserId) {
      return null;
    }

    if (requireChildFolder && !parentFolderId) {
      return null;
    }

    return {
      kind: "porteur",
      id: currentUserId,
      parentFolderId,
      forcedDocumentTypes,
      forceTypesFromCurrentFolder: forcedDocumentTypes.length > 0,
      label: parentFolderId ? "ce dossier porteur" : "vos documents porteur",
      helper: parentFolderId
        ? "Le dossier sera créé dans le dossier actuellement ouvert."
        : forcedDocumentTypes.length > 0
          ? "Le dossier sera créé dans le dossier actuellement ouvert et héritera de ses types de document."
          : "Le dossier sera créé à la racine de vos documents porteur.",
    };
  }, [ownerId, requireChildFolder, searchParams, user]);

  const { mutateAsync: saveFolderAsync, isPending } = useMutateData({
    id: "",
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "fetchDataV2" &&
          typeof query.queryKey[1] === "string" &&
          query.queryKey[1].includes("/document-folders/tree"),
      });
      setOpen(false);
      setFolderName("");
      setSelectedDocumentTypes([]);
    },
  });

  const getDocumentTreeSnapshots = (): DocumentsCacheSnapshot => {
    const queries = queryClient.getQueryCache().findAll({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "fetchDataV2" &&
        typeof query.queryKey[1] === "string" &&
        query.queryKey[1].includes("/document-folders/tree"),
    });

    return queries.map((query) => ({
      queryKey: query.queryKey,
      data: queryClient.getQueryData(query.queryKey),
    }));
  };

  const restoreDocumentTreeSnapshots = (snapshots: DocumentsCacheSnapshot) => {
    snapshots.forEach((snapshot) => {
      queryClient.setQueryData(snapshot.queryKey, snapshot.data);
    });
  };

  const setDocumentTreeQueriesData = (
    updater: (previous: unknown) => unknown,
  ) => {
    queryClient.setQueriesData(
      {
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "fetchDataV2" &&
          typeof query.queryKey[1] === "string" &&
          query.queryKey[1].includes("/document-folders/tree"),
      },
      updater,
    );
  };

  const handleOpen = () => {
    if (!folderTarget) return;
    setSelectedDocumentTypes(folderTarget.forcedDocumentTypes ?? []);
    setOpen(true);
  };

  const handleCreate = () => {
    if (!folderTarget || folderTarget.disabled) return;

    const trimmedName = folderName.trim() || "Nouveau dossier";
    const optimisticFolderId = Date.now();
    const optimisticFolder = {
      id: optimisticFolderId,
      name: trimmedName,
      scopeType: folderTarget.kind,
      scopeId: folderTarget.id,
      parentId: folderTarget.parentFolderId ?? null,
      defaultKey: folderTarget.defaultKey ?? null,
      documentTypes: folderTarget.forceTypesFromCurrentFolder
        ? (folderTarget.forcedDocumentTypes ?? [])
        : selectedDocumentTypes,
      children: [],
    };
    const snapshots = getDocumentTreeSnapshots();

    setDocumentTreeQueriesData((previous) =>
      addFolderToCollection(previous, optimisticFolder, folderTarget),
    );
    setOpen(false);
    setFolderName("");
    setSelectedDocumentTypes(folderTarget?.forcedDocumentTypes ?? []);

    void (async () => {
      try {
        const savedFolder: any = await saveFolderAsync({
          endpoint: `${import.meta.env.VITE_API_ENDPOINT}/document-folders/save`,
          body: {
            scopeType: folderTarget.kind,
            scopeId: folderTarget.id,
            documentTypes: folderTarget.forceTypesFromCurrentFolder
              ? (folderTarget.forcedDocumentTypes ?? [])
              : selectedDocumentTypes,
            parentId: folderTarget.parentFolderId ?? null,
            defaultKey: folderTarget.defaultKey,
            reuseByDefaultKey: folderTarget.reuseByDefaultKey,
            name: trimmedName,
          },
        });
        const persistedFolder =
          savedFolder?.data && typeof savedFolder.data === "object"
            ? savedFolder.data
            : savedFolder;
        if (persistedFolder?.id) {
          setDocumentTreeQueriesData((previous) =>
            replaceFolderInCollection(previous, optimisticFolderId, {
              ...persistedFolder,
              name: persistedFolder.name ?? trimmedName,
            }),
          );
        }
        toast({
          title: "Dossier ajouté",
          description: `"${trimmedName}" est maintenant disponible dans vos documents.`,
          duration: 3500,
        });
      } catch (_error) {
        restoreDocumentTreeSnapshots(snapshots);
        // Error feedback is already handled by the shared mutation hook.
      }
    })();
  };

  if (!folderTarget) {
    return null;
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="h-10 rounded-xl border-[#d9d1bb] bg-white text-[#5f5a49] hover:bg-[#f4f2ea]"
        onClick={handleOpen}
        disabled={folderTarget.disabled}
      >
        <FolderPlus className="mr-2 h-4 w-4" />
        Ajouter un dossier
      </Button>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setFolderName("");
            setSelectedDocumentTypes(folderTarget?.forcedDocumentTypes ?? []);
          }
        }}
      >
        <DialogContent className="sm:max-w-[720px] min-w-0">
          <DialogHeader>
            <DialogTitle>Ajouter un dossier</DialogTitle>
            <DialogDescription>
              Créez un dossier dans <span className="font-medium text-foreground">{folderTarget.label}</span>.
              <br />
              {folderTarget.helper}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nom du dossier</label>
              <Input
                value={folderName}
                onChange={(event) => setFolderName(event.target.value)}
                placeholder="Ex. Pièces administratives"
              />
            </div>
            {folderTarget.forceTypesFromCurrentFolder ? (
              <div className="space-y-2">
                <label className="text-sm font-medium">Types de document associés</label>
                <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  Le dossier héritera automatiquement des types du dossier courant pour apparaître dedans.
                </p>
              </div>
            ) : null}
            {/*
              Optionnel désactivé à la demande :
              sélection manuelle des types de document associés au dossier.
            */}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
                setFolderName("");
                setSelectedDocumentTypes([]);
              }}
            >
              Annuler
            </Button>
            <Button type="button" onClick={handleCreate} disabled={isPending}>
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddDocumentFolderButton;
